import React from 'react';
import styled from 'styled-components';
import { formatCurrency, formatNumberForDisplay } from '../utils/formatters';
import { formatHoraForDisplay } from '../utils/dateHelpers';
import { getMontoDelta } from '../utils/duplicateDetection';
import { getTitulosInstruccion, describirTitulos } from '../utils/instruccionesTitulos';

// --- Styled Components ---
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(17, 24, 39, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background-color: ${props => props.theme.colors.background};
  padding: 2.5rem;
  border-radius: 12px;
  width: 90%;
  max-width: 640px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${props => props.theme.shadows.soft};
  border: 3px solid ${props => props.theme.colors.warning};
`;

const WarningIcon = styled.div`
  width: 70px;
  height: 70px;
  margin: 0 auto 1.5rem;
  background-color: ${props => props.theme.colors.warning};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
`;

const ModalTitle = styled.h2`
  font-family: ${props => props.theme.typography.titleFamily};
  font-size: 1.75rem;
  color: ${props => props.theme.colors.secondary};
  margin: 0 0 1rem 0;
  text-align: center;
`;

const WarningText = styled.p`
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 1.05rem;
  color: ${props => props.theme.colors.secondary};
  margin: 0 0 1.5rem 0;
  text-align: center;
  line-height: 1.6;
`;

const SectionLabel = styled.p`
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${props => props.theme.colors.textSecondary};
  margin: 0 0 0.5rem 0;
`;

const InfoBox = styled.div`
  background-color: ${props => props.theme.colors.surface};
  padding: 1.25rem;
  border-radius: ${props => props.theme.borderRadius};
  margin-bottom: 1.25rem;
  border-left: 4px solid ${props => props.$accent === 'primary'
    ? props.theme.colors.primary
    : props.theme.colors.warning};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.6rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.secondary};
  font-size: 0.9rem;
`;

const InfoValue = styled.span`
  color: ${props => props.theme.colors.secondary};
  font-size: 0.9rem;
  text-align: right;
  max-width: 65%;
  word-break: break-word;
`;

const DeltaValue = styled(InfoValue)`
  font-weight: 700;
  color: ${props => props.theme.colors.errorText};
`;

const MatchHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const MatchId = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 24px;
  padding: 0 0.5rem;
  border-radius: 999px;
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.textPrimary};
  font-size: 0.8rem;
  font-weight: 700;
`;

const MatchMeta = styled.span`
  font-size: 0.8rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const Button = styled.button`
  font-family: ${props => props.theme.typography.fontFamily};
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: ${props => props.theme.borderRadius};
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 130px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.soft};
  }
`;

const CancelButton = styled(Button)`
  background-color: ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.secondary};

  &:hover {
    background-color: #D1D5DB;
  }
`;

const EditButton = styled(Button)`
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.textPrimary};
`;

const ContinueButton = styled(Button)`
  background-color: ${props => props.theme.colors.warning};
  color: #B45309;

  &:hover {
    background-color: rgba(252, 211, 77, 0.45);
  }
`;

// --- Helpers de presentación ---
const ENTITY_COPY = {
    instruccion: { singular: 'instrucción', plural: 'instrucciones' },
    operacion: { singular: 'operación', plural: 'operaciones' },
};

// Descripción corta del instrumento: fondo para FCI, especie para títulos.
const getInstrumentoLabel = (item, entity) => {
    if (entity === 'operacion') return item?.especie || '-';
    return item?.fci || item?.especie || '-';
};

const formatMonto = (item) => {
    if (item?.esRescateTotal) return 'Rescate Total';
    if (item?.monto) return formatCurrency(item.monto, item.moneda);
    // Una transferencia de varias especies se muestra con la cantidad de cada una.
    const titulos = getTitulosInstruccion(item);
    if (titulos.length > 1) return describirTitulos(titulos);
    if (item?.cantidad) return `${formatNumberForDisplay(item.cantidad)} VN`;
    return '-';
};

const formatDelta = (delta, moneda) => {
    const signo = delta.absoluto > 0 ? '+' : '-';
    const monto = formatCurrency(Math.abs(delta.absoluto), moneda);
    const porcentaje = `${delta.porcentaje > 0 ? '+' : '-'}${formatNumberForDisplay(Math.abs(delta.porcentaje), 2)} %`;
    return `${signo}${monto} (${porcentaje})`;
};

/**
 * Aviso de posible duplicado. Se usa en dos momentos:
 *  - mode="alta": antes de guardar una carga nueva.
 *  - mode="procesamiento": antes de dejar en "Hecha" una fila marcada.
 * Nunca bloquea: siempre ofrece "Continuar de todos modos".
 */
const DuplicateWarningModal = ({
    isOpen,
    mode = 'alta',
    entity = 'instruccion',
    candidate,
    matches = [],
    onEditMatch,
    onContinue,
    onCancel,
}) => {
    if (!isOpen || !matches.length) return null;

    const copy = ENTITY_COPY[entity] || ENTITY_COPY.instruccion;
    const esAlta = mode === 'alta';
    const cantidad = matches.length;
    const sustantivo = cantidad === 1 ? copy.singular : copy.plural;
    const puedeEditar = esAlta && cantidad === 1 && typeof onEditMatch === 'function';

    const title = esAlta
        ? `¿Ya cargaste esta ${copy.singular}?`
        : `Posible ${copy.singular} duplicada`;

    const description = esAlta
        ? `Ya hay ${cantidad} ${sustantivo} de hoy con el mismo cliente, tipo e instrumento. Si es la misma, editá la existente en vez de cargar una nueva.`
        : `Estás por marcar como Hecha una ${copy.singular} que coincide con ${cantidad === 1 ? 'otra' : `otras ${cantidad}`} ya cargada${cantidad === 1 ? '' : 's'}. Procesar las dos deja al cliente deudor.`;

    return (
        <ModalOverlay>
            <ModalContent>
                <WarningIcon>⚠️</WarningIcon>
                <ModalTitle>{title}</ModalTitle>
                <WarningText>{description}</WarningText>

                {candidate && (
                    <>
                        <SectionLabel>{esAlta ? 'Estás cargando' : 'Estás procesando'}</SectionLabel>
                        <InfoBox $accent="primary">
                            <InfoRow>
                                <InfoLabel>Cliente:</InfoLabel>
                                <InfoValue>{candidate.cliente || candidate.nroComitente || '-'}</InfoValue>
                            </InfoRow>
                            <InfoRow>
                                <InfoLabel>{entity === 'operacion' ? 'Operación:' : 'Tipo:'}</InfoLabel>
                                <InfoValue>{candidate.tipo || candidate.operacion || '-'}</InfoValue>
                            </InfoRow>
                            <InfoRow>
                                <InfoLabel>{entity === 'operacion' ? 'Especie:' : 'Instrumento:'}</InfoLabel>
                                <InfoValue>{getInstrumentoLabel(candidate, entity)}</InfoValue>
                            </InfoRow>
                            <InfoRow>
                                <InfoLabel>Monto:</InfoLabel>
                                <InfoValue style={{ fontWeight: 700 }}>{formatMonto(candidate)}</InfoValue>
                            </InfoRow>
                        </InfoBox>
                    </>
                )}

                <SectionLabel>{cantidad === 1 ? 'Ya cargada' : 'Ya cargadas'}</SectionLabel>
                {matches.map((match) => {
                    const delta = getMontoDelta(candidate?.monto, match?.monto);
                    return (
                        <InfoBox key={match.id}>
                            <MatchHeader>
                                <MatchId>#{match.persistentId ?? '?'}</MatchId>
                                <MatchMeta>
                                    {match.estado}
                                    {match.comercial ? ` · ${match.comercial}` : ''}
                                    {match.createdAt ? ` · ${formatHoraForDisplay(match.createdAt)}` : ''}
                                </MatchMeta>
                            </MatchHeader>
                            <InfoRow>
                                <InfoLabel>Monto cargado:</InfoLabel>
                                <InfoValue style={{ fontWeight: 700 }}>{formatMonto(match)}</InfoValue>
                            </InfoRow>
                            {delta && (
                                <InfoRow>
                                    <InfoLabel>Diferencia:</InfoLabel>
                                    <DeltaValue>{formatDelta(delta, match.moneda)}</DeltaValue>
                                </InfoRow>
                            )}
                            {match.middleOffice && (
                                <InfoRow>
                                    <InfoLabel>Middle Office:</InfoLabel>
                                    <InfoValue>{match.middleOffice}</InfoValue>
                                </InfoRow>
                            )}
                            {match.trader && (
                                <InfoRow>
                                    <InfoLabel>Trader:</InfoLabel>
                                    <InfoValue>{match.trader}</InfoValue>
                                </InfoRow>
                            )}
                        </InfoBox>
                    );
                })}

                <ButtonContainer>
                    <CancelButton type="button" onClick={onCancel}>
                        Cancelar
                    </CancelButton>
                    {puedeEditar && (
                        <EditButton type="button" onClick={() => onEditMatch(matches[0])}>
                            Editar la #{matches[0].persistentId} existente
                        </EditButton>
                    )}
                    <ContinueButton type="button" onClick={onContinue}>
                        Continuar de todos modos
                    </ContinueButton>
                </ButtonContainer>
            </ModalContent>
        </ModalOverlay>
    );
};

export default DuplicateWarningModal;
